/**
 * ThinkingOverlay - Cursor-Style AI Thinking Preview
 * 
 * Shows the AI's reasoning and tool activities as ghost/preview text
 * that fades into the final response. Provides visibility into the
 * AI's thought process during document editing.
 * 
 * Features:
 * - Preview text in muted/ghost styling (opacity 0.6, italic)
 * - Inline activity log with icons and explanations
 * - Fade-to-solid transition on completion
 * - Chain/turn indicators for Agent mode
 */

import { useEffect, useRef, useState } from 'react';
import './ThinkingOverlay.css';

export type ThinkingPhase = 'idle' | 'thinking' | 'executing' | 'solidifying' | 'complete';

export interface ThinkingActivity {
  id: string;
  type: 'planning' | 'executing' | 'complete' | 'tool_start' | 'tool_complete';
  icon: string;
  message: string;
  toolName?: string;
  timestamp: Date;
}

export interface ThinkingOverlayProps {
  /** Whether the AI is currently processing */
  isProcessing: boolean;
  /** Preview text streaming from the AI (before solidification) */
  previewText: string;
  /** Current thinking phase */
  phase: ThinkingPhase;
  /** Activity log items */
  activities: ThinkingActivity[];
  /** Chain count for Agent mode (Step Functions iterations) */
  chainCount?: number;
  /** Turn count within current chain */
  turnCount?: number;
  /** Elapsed time in ms */
  elapsedMs?: number;
  /** Whether this is Agent mode (uses Step Functions) */
  isAgentMode?: boolean;
  /** Callback when solidification animation completes */
  onSolidifyComplete?: () => void;
}

export function ThinkingOverlay({
  isProcessing,
  previewText,
  phase,
  activities,
  chainCount: _chainCount = 0,
  turnCount: _turnCount = 0,
  elapsedMs: _elapsedMs = 0,
  isAgentMode: _isAgentMode = false,
  onSolidifyComplete,
}: ThinkingOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSolidifying, setIsSolidifying] = useState(false);
  
  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [previewText, activities]);
  
  // Handle solidification transition
  useEffect(() => {
    if (phase === 'solidifying') {
      setIsSolidifying(true);
      // After transition completes, notify parent
      const timer = setTimeout(() => {
        onSolidifyComplete?.();
      }, 300); // Match CSS transition duration
      return () => clearTimeout(timer);
    } else if (phase === 'idle' || phase === 'thinking') {
      setIsSolidifying(false);
    }
  }, [phase, onSolidifyComplete]);
  
  // Hide activities once response text starts flowing
  // Show activities only when we're in tool execution phase (no text yet)
  const showActivities = activities.length > 0 && previewText.length < 20;
  
  // Format elapsed time
  const _formatElapsed = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };
  
  // Don't render if not processing and no preview
  if (!isProcessing && phase === 'idle' && !previewText) {
    return null;
  }
  
  return (
    <div 
      ref={containerRef}
      className="thinking-overlay"
    >
      {/* Chain/turn indicators removed for cleaner UX */}
      
      {/* Activity Log */}
      {showActivities && activities.length > 0 && (
        <div className="thinking-activities">
          {activities.slice(-5).map((activity) => (
            <div 
              key={activity.id}
              className={`thinking-activity ${
                activity.type === 'complete' || activity.type === 'tool_complete' 
                  ? 'opacity-60' 
                  : ''
              }`}
            >
              <span className="thinking-activity-icon">{activity.icon}</span>
              <span className="thinking-activity-message">{activity.message}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Preview Text (Ghost Text) */}
      {previewText && (
        <div 
          className={`thinking-preview ${isSolidifying ? 'solidifying' : ''}`}
        >
          {previewText}
        </div>
      )}
      
      {/* Loading indicator when processing but no text yet */}
      {isProcessing && !previewText && activities.length === 0 && (
        <div className="thinking-loading">
          <span className="inline-block w-2 h-2 bg-secondary rounded-full animate-bounce" />
          {/* eslint-disable-next-line no-restricted-syntax -- Animation delay for staggered bounce effect */}
          <span className="inline-block w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
          {/* eslint-disable-next-line no-restricted-syntax -- Animation delay for staggered bounce effect */}
          <span className="inline-block w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
        </div>
      )}
    </div>
  );
}

export default ThinkingOverlay;

